import { EditUserProfileLoader } from "@/components/EditUserProfile/EditUserProfile";
import { RouterErrorBoundary } from "@/components/ErrorBoundary/RouteErrorBoundary";
import { AdminLayout } from "@/layouts/AdminLayout";
import { HomePageLayout } from "@/layouts/HomePageLayout";
import { AdminEventPlanningPage } from "@/pages/admin/AdminEventPlanning.page";
import { AdminUsersPage } from "@/pages/admin/AdminUsers.page";
import { MagicLinkPage } from "@/pages/auth/MagicLink.page";
import { UpdatePasswordPage } from "@/pages/auth/UpdatePassword.page";
import { CheckEmailPage } from "@/pages/CheckEmail.page";
import EmailConfirmation from "@/pages/EmailConfirmation.page";
import EventConfirmationPage from "@/pages/EventConfirmation.page";
import { EventReservationPage } from "@/pages/EventReservation.page";
import { HelpCenterPage } from "@/pages/home/HelpCenter.page";
import { HomePage } from "@/pages/home/Home.page";
import { HomeMyReservationsPage } from "@/pages/home/HomeMyReservations.page";
import { HomeProfilePage } from "@/pages/home/HomeProfile.page";
import { PersonalityTestAnswersPage } from "@/pages/home/PersonalityTestAnswers.page";
import { LandingPage } from "@/pages/Landing.page";
import { EmailLoginPage } from "@/pages/login/EmailLogin.page";
import { LogoutPage } from "@/pages/Logout.page";
import OauthRedirect from "@/pages/OauthRedirect.page";
import { PaymentCancelPage } from "@/pages/payment/PaymentCancel.page";
import { PaymentFailPage } from "@/pages/payment/PaymentFail.page";
import { PaymentSuccessPage } from "@/pages/payment/PaymentSuccess.page";
import { PaymentTestPage } from "@/pages/payment/PaymentTest.page";
import { PersonalityTestPage } from "@/pages/PersonalityTestPage/PersonalityTest.page";
import { ReservationChangePage } from "@/pages/reservationChange/ReservationChange.page";
import { ReservationChangeDeclinedPage } from "@/pages/reservationChangeDeclined/ReservationChangeDeclined.page";
import { ReservationChangeRequestPage } from "@/pages/reservationChangeRequest/ReservationChangeRequest.page";
import { EmailSignupPage } from "@/pages/signup/EmailSignup.page";
import { KakaoSignupPage } from "@/pages/signup/KakaoSignup.page";
import { SignupPage } from "@/pages/signup/Signup.page";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { PrimaryMobileLayout } from "../layouts/PrimaryMobileLayout";
import { AnonymousRoute } from "./AnonymousRoute";
import { HasNotOnboardedRoute } from "./HasNotOnboardedRoute";
import { ProtectedRoute } from "./ProtectedRoute";

const router = createBrowserRouter([
  {
    path: "/",
    element: <PrimaryMobileLayout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <AnonymousRoute>
            <LandingPage />
          </AnonymousRoute>
        ),
      },
      {
        path: "login",
        element: (
          <AnonymousRoute>
            <Outlet />
          </AnonymousRoute>
        ),
        children: [
          {
            path: "email",
            element: <EmailLoginPage />,
          },
        ],
      },
      {
        path: "signup",
        element: (
          <HasNotOnboardedRoute>
            <Outlet />
          </HasNotOnboardedRoute>
        ),
        children: [
          {
            index: true,
            element: <SignupPage />,
          },
          {
            path: "email",
            element: <EmailSignupPage />,
          },
          {
            path: "kakao",
            element: <KakaoSignupPage />,
          },
        ],
      },
      {
        path: "check-email",
        element: (
          <AnonymousRoute>
            <CheckEmailPage />
          </AnonymousRoute>
        ),
      },
      // users are free to log in before OR after they've taken the test,
      // so we don't need to require them to be authenticated here
      { path: "personality-test", element: <PersonalityTestPage /> },
      { path: "oauth-redirect", element: <OauthRedirect /> },
      { path: "confirm-email", element: <EmailConfirmation /> },
      { path: "l/:token", element: <MagicLinkPage /> },
      { path: "update-password", element: <UpdatePasswordPage /> },

      // authenticated routes
      {
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          // manual logout
          {
            path: "logout",
            element: <LogoutPage />,
          },
          { path: "event-reservation", element: <EventReservationPage /> },
          { path: "event-confirmation", element: <EventConfirmationPage /> },
        ],
      },
      {
        path: "home",
        element: (
          <ProtectedRoute>
            <HomePageLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <HomePage /> },
          { path: "reservation", element: <HomeMyReservationsPage /> },
          { path: "reservation/upcoming", element: <HomeMyReservationsPage /> },
          { path: "reservation/upcoming/:reservationId", element: <HomeMyReservationsPage /> },
          { path: "reservation/past", element: <HomeMyReservationsPage /> },
          { path: "reservation/past/:reservationId", element: <HomeMyReservationsPage /> },
          { path: "profile", element: <HomeProfilePage /> },
          { path: "profile/edit", element: <EditUserProfileLoader /> },
          { path: "profile/personality-test", element: <PersonalityTestAnswersPage /> },
          { path: "profile/help", element: <HelpCenterPage /> },
        ],
      },
      {
        path: "payment",
        children: [
          { path: "test", element: <PaymentTestPage /> },
          { path: "success", element: <PaymentSuccessPage /> },
          { path: "cancel", element: <PaymentCancelPage /> },
          { path: "fail", element: <PaymentFailPage /> },
        ],
      },
      {
        path: "reservation-change/:reservationId",
        element: <ReservationChangePage />,
      },
      {
        path: "reservation-change-request/:reservationId",
        element: (
          <ProtectedRoute>
            <ReservationChangeRequestPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "reservation-change-declined",
        element: (
          <ProtectedRoute>
            <ReservationChangeDeclinedPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: "/admin",
    element: (
      <ProtectedRoute>
        <AdminLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: "eventManagement",
        element: <AdminEventPlanningPage />,
      },
      {
        path: "users",
        element: <AdminUsersPage />,
      },
    ],
  },
]);

export function Router() {
  return <RouterProvider router={router} />;
}
